import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Container, Row, Col, media } from 'styled-bootstrap-grid'
import { Link } from 'react-router-dom'
import getRelativeUrl from 'utils/getRelativeUrl'
import Product from 'ui/components/modules/Products/Product'
import Text from './Text'
import theme from 'ui/styles/theme'
import VideoCover from 'react-video-cover'
export default class Hero extends Component {
  constructor(props) {
    super(props)
    this.blurRef = React.createRef()
    this.featuredContainerRef = React.createRef()
  }

  static propTypes = {
    title: PropTypes.string,
    heading: PropTypes.string,
    video: PropTypes.object,
    image: PropTypes.object,
    button1: PropTypes.object,
    button2: PropTypes.object,
    featured: PropTypes.array.isRequired,
  }

  static defaultProps = {
    title: '',
    heading: '',
    video: null,
    image: null,
    button1: null,
    button2: null,
  }

  state = {
    overlayOpacity: 1,
    isMobile: false
  }

  componentDidMount() {
    this.detectMobile()
    
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.detectMobile)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.detectMobile)
  }

  detectMobile = () => {
    const width = window.innerWidth
    const isMobile = width <= theme.grid.breakpoints.md

    if (isMobile !== this.state.isMobile) {
      this.setState({ isMobile })
    }
  }

  handleScroll = () => {
    const startPos = window.innerHeight / 8
    const endPos = window.innerHeight / 4
    const range = Math.floor(endPos - startPos)
    const relativePos = Math.floor(window.scrollY - startPos)
    const relativePct = Math.floor((relativePos * 100) / range)
    const blurMax = 20
    const transformMax = 75

    const blurVal = blurMax * (relativePct / 100)
    const transformVal = transformMax * (relativePct / 100) + 75
    const opacityVal = 1 - (relativePct / 100)

    if(window.scrollY > startPos && window.scrollY < endPos) {
      window.requestAnimationFrame(() => {
        if(!this.props.video) {
          this.blurRef.current.style.filter = `blur(${blurVal}px)`
        }

        this.featuredContainerRef.current.style.transform = `translateY(-${transformVal}px)`

        this.setState({overlayOpacity: opacityVal})
      })
    } else if (window.scrollY > endPos) {
      window.requestAnimationFrame(() => {
        if(!this.props.video) {
          this.blurRef.current.style.filter = `blur(${blurMax}px)`
        }

        this.featuredContainerRef.current.style.transform = `translateY(-${transformMax * 2}px)`

        this.setState({overlayOpacity: 0})
      })
    } else if (window.scrollY < startPos) {
      window.requestAnimationFrame(() => {
        if(!this.props.video) {
          this.blurRef.current.style.filter = `blur(0px)`
        }

        this.featuredContainerRef.current.style.transform = `translateY(-${transformMax}px)`

        this.setState({overlayOpacity: 1})
      })
    }
  }

  render() {
    const {
      heading,
      title,
      button1,
      button2,
      featured,
      video,
      videoMobile
    } = this.props

    const { isMobile } = this.state

    const colors = [
      `${theme.colors.tones.lightest}`,
      `${theme.colors.tones.lighter}`,
      `${theme.colors.secondary}`
    ]

    const videoOptions = {
      src: isMobile ? (videoMobile && videoMobile.sourceUrl) : (video && video.sourceUrl),
      autoPlay: 'autoplay',
      playsInline: 'playsInline',
      muted: 'muted',
      loop: 'loop'
    }

    return (
      <div>
        <Wrapper>
          <TopContainer>
            <Container fluid>
              <Row>
                <Col
                  sm={6}
                  smOffset={6}
                >
                  <TextContainer>
                    <Title>{title}</Title>
                    <Heading>{heading}</Heading>
                    <ButtonContainer>
                      {button1 &&
                        <Button to={getRelativeUrl(button1.url)}>{button1.title}</Button>
                      }
                      {button2 &&
                        <Button to={getRelativeUrl(button2.url)}>{button2.title}</Button>
                      }
                    </ButtonContainer>
                  </TextContainer>
                </Col>
              </Row>
            </Container>
            {(video && videoMobile) ?
              <Video videoOptions={videoOptions} />
              :
              <Image
                src={this.props.image.sourceUrl}
                innerRef={this.blurRef}
              />
            }
          </TopContainer>
        </Wrapper>
        <FeaturedWrapper innerRef={this.featuredContainerRef}>
          <FeaturedContainer fluid>
            {featured.map((box, index) => {
              if(box.type === 'product') {
                return(
                  <FeaturedContent key={index}>
                    <Product
                      noMargin
                      showOverlay
                      overlayColor={colors[index]}
                      overlayOpacity={this.state.overlayOpacity}
                      title={box.product.title}
                      link={box.product.link}
                      video={box.product.video.sourceUrl}
                      image={box.product.image.sourceUrl}
                    />
                  </FeaturedContent>
                )
              } else {
                return(
                  <FeaturedContent key={index}>
                    <Text
                      heading={box.heading}
                      text={box.paragraph}
                      link={box.link.url}
                      externalLink={box.externalLink}
                      overlayColor={colors[index]}
                      overlayOpacity={this.state.overlayOpacity}
                    />
                  </FeaturedContent>
                )
              }
            })}
          </FeaturedContainer>
        </FeaturedWrapper>
      </div>
    )
  }
}

// Styles
const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
`

const TopContainer = styled.div`
  width: 100%;
  height: 100vh;
`

const TextContainer = styled.div`
  position: relative;
  z-index: 1;
  border-top: 1px solid white;
  color: white;
  margin-top: 40%;
`

const Title = styled.h2`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  margin-top: 20px;
`

const Heading = styled.h1`
  font-weight: 300;
  font-size: 35px;
  line-height: 40px;
  margin-bottom: 0;
  margin-top: 0.85em;

  ${media.sm`
    font-size: 80px;
    line-height: 80px;
  `}
`

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 2em;

  ${media.sm`
    margin-top: 48px;
  `}
`

const Button = styled(Link)`
  font-size: 12px;
  font-weight: 500;
  padding: 15px 25px;
  text-transform: uppercase;
  color: white;
  border: 1px solid white;
  border-radius: 30px;
  margin-right: 20px;
  margin-bottom: 10px;
  white-space: nowrap;
`

const FeaturedWrapper = styled.div`
  position: relative;
  z-index: 1;
  transform: translateY(-75px);
`

const FeaturedContainer = styled(Container)`
  display: flex;
  flex-wrap: wrap;
`

const FeaturedContent = styled.div`
  width: 100%;
  position: relative;
  background: white;
  margin-top: 5%;

  ${media.lg`
    flex: 1;
    margin-left: 1%;
    margin-top: 5%;
  `}
`

const Video = styled(VideoCover)`
  display: block;
  position: absolute !important;
  top: 0;
  left: 0;
  z-index: 0;
`

const Image = styled.img`
  width: 110%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: absolute;
  top: 0;
  left: -5%;
  z-index: 0;
`



// WEBPACK FOOTER //
// ./src/ui/components/modules/Hero/index.js